// frontend/src/pages/admin/EmailTemplatesAdmin.tsx
import React, { useMemo, useState } from "react";
import { Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { IconDownload, IconUpload } from "@tabler/icons-react";
import { useAppDispatch } from "../../../hooks";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { FailedAlert, MCLoader } from "../../../components";
import {
  useGetEmailsTemplatesQuery,
  usePostEmailsTemplatesUploadZipMutation,
  useGetEmailsTemplatesDownloadQuery,
} from "../../../../openapi";
import { EMAIL_TEMPLATE_KEYS, IEmailTemplateKey } from "@mcapps/shared/emailSchema";
import { downloadBase64File, readFileAsBase64 } from "../../fileHandlerUtils";
import { StatusExistPill } from "../../../components/StatusExistPill";

const ZIP_CONTENT_TYPE = "application/zip" as const;
const ZIP_CONTENT_TYPES = new Set(["application/zip", "application/x-zip-compressed", "application/octet-stream"]);

export const EmailTemplatesAdmin: React.FC = () => {
  const dispatch = useAppDispatch();

  const { data, isLoading, isError, refetch } = useGetEmailsTemplatesQuery();

  const [uploadZip, uploadState] = usePostEmailsTemplatesUploadZipMutation();

  const [downloadKey, setDownloadKey] = useState<IEmailTemplateKey | null>(null);
  const {
    data: downloadData,
    isFetching: isDownloading,
    isError: isDownloadError,
  } = useGetEmailsTemplatesDownloadQuery(downloadKey ? { templateKey: downloadKey } : (undefined as any), {
    skip: downloadKey == null,
  });

  React.useEffect(() => {
    if (!downloadData) return;
    try {
      downloadBase64File(downloadData.filename, downloadData.base64, downloadData.contentType);
    } finally {
      setDownloadKey(null);
    }
  }, [downloadData]);

  const metaByKey = useMemo(() => {
    const m = new Map<string, any>();
    for (const t of data?.templates ?? []) m.set(t.templateKey, t);
    return m;
  }, [data]);

  const upload = async (templateKey: IEmailTemplateKey, e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.currentTarget.files?.[0];
      if (!file) return;

      e.currentTarget.value = "";

      if (file.type && !ZIP_CONTENT_TYPES.has(file.type)) {
        throw new Error(`Ugyldig filtype: ${file.type}`);
      }
      if (!file.name.toLowerCase().endsWith(".zip")) {
        throw new Error("Filen må være en .zip");
      }

      const base64 = await readFileAsBase64(file);

      await uploadZip({
        body: {
          templateKey,
          file: {
            filename: file.name,
            contentType: ZIP_CONTENT_TYPE,
            base64,
          },
        },
      }).unwrap();

      dispatch(setShowSuccessMessageThunk(`Malen er lastet opp: ${templateKey}`));
      await refetch();
    } catch (err: any) {
      console.error(err);
      dispatch(setShowSuccessMessageThunk(`Feil: ${err?.message ?? String(err)}`));
    }
  };

  const download = (templateKey: IEmailTemplateKey) => {
    setDownloadKey(templateKey);
  };

  const hasError = isError || uploadState.isError || isDownloadError;

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={4}>E-postmaler (ZIP)</Title>
          <Button variant="light" onClick={() => refetch()} disabled={isLoading}>
            Oppdater
          </Button>
        </Group>

        {(isLoading || uploadState.isLoading || isDownloading) && <MCLoader />}
        {hasError && <FailedAlert />}

        <Stack gap="sm">
          {EMAIL_TEMPLATE_KEYS.map((templateKey) => {
            const meta = metaByKey.get(templateKey);
            const allRequiredFilesExist = Boolean(meta?.allRequiredFilesExist);

            const files: Array<{ filename: string; exists: boolean }> = meta?.files ?? [];

            return (
              <Card key={templateKey} withBorder shadow="sm" p="md">
                <Group justify="space-between" align="center">
                  <Stack gap={6}>
                    <Text fw={600}>{templateKey}</Text>

                    <Group gap="xs">
                      <StatusExistPill
                        exists={allRequiredFilesExist}
                        label={allRequiredFilesExist ? "Komplett" : "Ufullstendig"}
                      />
                    </Group>

                    {files.length > 0 && (
                      <Group gap="xs">
                        {files.map((f) => (
                          <StatusExistPill key={f.filename} exists={f.exists} label={f.filename} />
                        ))}
                      </Group>
                    )}
                  </Stack>

                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={16} />}
                      disabled={!allRequiredFilesExist}
                      onClick={() => download(templateKey)}
                    >
                      Last ned
                    </Button>

                    <label>
                      <input
                        type="file"
                        hidden
                        accept=".zip,application/zip,application/x-zip-compressed"
                        onChange={(e) => upload(templateKey, e)}
                      />
                      <Button
                        size="xs"
                        variant="filled"
                        leftSection={<IconUpload size={16} />}
                        disabled={uploadState.isLoading}
                        component="span"
                      >
                        Last opp ZIP
                      </Button>
                    </label>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </Stack>

        <Text size="sm" c="dimmed">
          ZIP-innhold valideres mot fil-listen for hver mal. Opplasting overskriver eksisterende filer i blob.
        </Text>
      </Stack>
    </Card>
  );
};
